import React, { FC, useState } from 'react';
import { useSetState } from 'react-use';
import { Button, Modal, Spacing } from 'sugar-design';

import { PolicyContent } from './type';
import useCountDown from './useCountDown';
import { getI18n } from '@/utils/i18n';

import styles from './PcPolicyPreviewModal.scss';

interface PcPolicyPreviewModalProps {
  isOpen: boolean;
  privacyPolicy: PolicyContent[];
  isPreviewMode?: boolean;
  onClose: () => void;
  onAgree: () => void;
}

const i18n = getI18n();

const PcPolicyPreviewModal: FC<PcPolicyPreviewModalProps> = (props) => {
  const { onAgree, onClose, isOpen, privacyPolicy, isPreviewMode } = props;
  const [{ currentIndex, currentPolicy }, setCurrentPolicy] = useSetState({
    currentIndex: 0,
    currentPolicy: privacyPolicy[0],
  });

  const [hasRead, setHasRead] = useState(false);

  const [countDown, resetCount] = useCountDown({
    leftTime: 3000,
    onEnd: () => {
      setHasRead(true);
    },
  });

  const policyLength = privacyPolicy.length;

  const viewNextPolicy = () => {
    const nextIndex = currentIndex + 1;
    setCurrentPolicy({ currentIndex: nextIndex, currentPolicy: privacyPolicy[nextIndex] });

    if (currentPolicy.checkTiming) {
      setHasRead(false);
      resetCount();
    }
  };

  const onPreviewConfirm = () => {
    onClose();
  };

  const onConfirm = () => {
    onAgree();
    onClose();
  };

  const disableConfirm = !!(currentPolicy.checkTiming && !hasRead && !isPreviewMode);
  return (
    <Modal
      isOpen={isOpen}
      size="lg"
      customHeader={<div className={styles.policyHeader}>{currentPolicy.name}</div>}
      renderCustomFooter={() => {
        const renderButton = () => {
          // 多条隐私政策时，如果不在最后一条，显示查看下一条按钮
          if (policyLength > 1 && currentIndex < policyLength - 1) {
            return (
              <Button onClick={viewNextPolicy} disabled={disableConfirm}>
                {disableConfirm
                  ? i18n.t`我已阅读并同意（${countDown}S）`
                  : isPreviewMode
                  ? i18n.t`查看下一条`
                  : i18n.t`我已阅读并同意`}
              </Button>
            );
          }

          // 预览模式下，浏览最后一条，显示确认按钮
          if (isPreviewMode) {
            return <Button onClick={onPreviewConfirm}>{i18n.t`确认`}</Button>;
          }

          // 非预览模式下，浏览最后一条，需要确认是否同意协议
          return (
            <Button onClick={onConfirm} disabled={disableConfirm}>
              {disableConfirm ? i18n.t`我已阅读并同意（${countDown}S）` : i18n.t`我已阅读并同意`}
            </Button>
          );
        };

        return (
          <Spacing justify="end" stretched>
            {renderButton()}
          </Spacing>
        );
      }}
      autoHeight
      onClose={onClose}
    >
      {currentPolicy?.isDefault ? (
        <iframe src={currentPolicy.content} className={styles.secret} />
      ) : (
        <div key={`${currentIndex}_${currentPolicy.name}`} className={styles.scrollContainer}>
          <div
            dangerouslySetInnerHTML={{ __html: currentPolicy.content }}
            className={styles.htmlContentWrap}
          />
        </div>
      )}
    </Modal>
  );
};

PcPolicyPreviewModal.defaultProps = {
  isPreviewMode: false,
  requireRead: false,
};

export default PcPolicyPreviewModal;
